import React, { useMemo } from 'react';
import {
    getSparkTagVariant,
    SPARK_TAG_VARIANT_COLORS,
} from '@/features/datasets/components/DataSelection/sparkLabelColors';

/**
 * Predictions breakdown card: top-N label counts with progress bars.
 *
 * Expects ``predictions`` as an array of ``{ label, count }`` objects sorted
 * by count descending (shaped by the backend). Renders up to 10 labels.
 */
const PredictionsCard = ({ predictions = [] }) => {
    const total = predictions.reduce((acc, p) => acc + (p.count || 0), 0);
    const allLabels = useMemo(() => predictions.map((p) => p.label), [predictions]);

    const getColor = (label) => {
        const variant = getSparkTagVariant(label, allLabels);
        return SPARK_TAG_VARIANT_COLORS[variant] || SPARK_TAG_VARIANT_COLORS.blue;
    };

    if (!predictions.length) {
        return (
            <div className="metric-card">
                <div className="metric-card__label">Predictions</div>
                <div style={{ color: 'var(--semantic-content-secondary)', fontSize: 13, paddingTop: 8 }}>
                    No prediction data yet.
                </div>
            </div>
        );
    }

    return (
        <div className="metric-card">
            <div className="metric-card__label">Predictions</div>

            <div className="predictions-card__header">
                {predictions.map((p) => {
                    const { color } = getColor(p.label);
                    return (
                        <div key={p.label} className="predictions-card__count-item">
                            <span className="predictions-card__count-value" style={{ color }}>
                                {(p.count || 0).toLocaleString()}
                            </span>
                            <span className="predictions-card__count-label">
                                <span
                                    className="predictions-card__color-dot"
                                    style={{ background: color }}
                                />
                                {p.label}
                            </span>
                        </div>
                    );
                })}
            </div>

            <div className="predictions-card__bars">
                {predictions.map((p) => {
                    const pct = total > 0 ? Math.round((p.count / total) * 100) : 0;
                    const { color, bg } = getColor(p.label);
                    return (
                        <div key={p.label} className="predictions-card__bar-row">
                            <div className="predictions-card__bar-meta">
                                <span className="predictions-card__bar-label">{p.label}</span>
                                <span className="predictions-card__bar-count">
                                    {(p.count || 0).toLocaleString()} · {pct}%
                                </span>
                            </div>
                            <div className="predictions-card__progress-track">
                                <div
                                    className="predictions-card__progress-fill"
                                    style={{ width: `${pct}%`, background: bg, borderLeft: `3px solid ${color}` }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default PredictionsCard;
